import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { t } from '../../i18n';
import { toAuthFailure } from '../../lib/auth';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Field, FieldDescription, FieldLabel, FieldSeparator } from '../../ui/field';
import { Input } from '../../ui/input';
import { describeAuthFailure, useAuthSession } from '../auth/AuthProvider';
import { AuthFrame } from './AuthFrame';
import { GoogleButton } from './GoogleButton';
import { AUTH_ROUTE_PATHS, HOME_PATH } from './paths';

/** Self-serve account creation (platform design §17.2). Google is offered
 *  here too -- `signInWithGoogle` creates the account when the address has
 *  none yet, so it is the same second method §3/architecture S1 names for
 *  sign-in, not a separate flow. */
export function SignUp(): ReactNode {
  const { session } = useAuthSession();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Same landing spot as sign-in, for the same reason: the application root,
  // never a route picked by capability here (Spec §3) -- the shell mounted at
  // HOME_PATH is what resolves what a signed-in caller may do.
  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await session.signUp(email, password, name);
      await navigate({ to: HOME_PATH });
    } catch (cause) {
      setError(describeAuthFailure(toAuthFailure(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await session.signInWithGoogle();
      await navigate({ to: HOME_PATH });
    } catch (cause) {
      setError(describeAuthFailure(toAuthFailure(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthFrame
      title={t('auth.signUp')}
      footer={{
        prompt: t('auth.signIn.prompt'),
        to: AUTH_ROUTE_PATHS.signIn,
        label: t('auth.signIn'),
      }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
        <Field>
          <FieldLabel htmlFor="sign-up-name">{t('auth.name')}</FieldLabel>
          <Input
            id="sign-up-name"
            name="name"
            type="text"
            autoComplete="name"
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="sign-up-email">{t('auth.email')}</FieldLabel>
          <Input
            id="sign-up-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="sign-up-password">{t('auth.password')}</FieldLabel>
          <Input
            id="sign-up-password"
            name="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <FieldDescription>{t('auth.password.hint')}</FieldDescription>
        </Field>

        {error !== null && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={submitting}>
          {t('auth.signUp')}
        </Button>
      </form>

      <FieldSeparator>{t('auth.or')}</FieldSeparator>

      <GoogleButton onClick={() => void handleGoogle()} disabled={submitting} />
    </AuthFrame>
  );
}
